import { Fragment, useEffect, useState, useCallback } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import toast from "react-hot-toast";
import { mapErrors } from "../Utils";
import PMListboxFormik from "./PMListboxFormik";
import PMSwitch from "./PMSwitch";
import { checkPdfStatus } from "../../api/service/report";

/**
 * Generic Print Dialog Component
 * Can be used for Reports, Work Orders, and other modules that need printing functionality
 * 
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Callback when dialog closes
 * @param {Object} props.item - The item to print (report, workOrder, etc.)
 * @param {Function} props.onRefresh - Optional callback when print is successful
 * @param {Object} props.config - Configuration object
 * @param {string} props.config.module - Module name ('report' or 'work_order')
 * @param {string} props.config.title - Dialog title
 * @param {Array} props.config.printTypeOptions - Options for print type (e.g., ['report', 'customer_report', 'note'])
 * @param {Function} props.config.getPdfFunction - Function to get PDF preview
 * @param {Function} props.config.printFunction - Function to print/download
 * @param {string} props.config.printTypeParamName - Parameter name for print type (e.g., 'printReportType' or 'printWorkOrderType')
 */
export const PrintDialog = (props) => {
  const { open, onClose, item, onRefresh, config } = props;
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [selectedPrintType, setSelectedPrintType] = useState(null);
  const [includeImages, setIncludeImages] = useState(false);
  const [pollingAttempts, setPollingAttempts] = useState(0);

  const {
    module,
    title,
    printTypeOptions,
    getPdfFunction,
    printFunction,
    printTypeParamName,
    existingPdfUrl,
  } = config;

  const initialValues = {
    id: item?.id,
    [printTypeParamName]: "",
    include_images: false,
    submit: null,
  };

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setPdfUrl(null);
      setSelectedPrintType(null);
      setIncludeImages(false);
      setIsLoading(false);
      setIsPolling(false);
      setPollingAttempts(0);
    }
  }, [open]);

  // Removed automatic PDF generation - now triggered by button click

  const fetchPdf = useCallback(async () => {
    setIsLoading(true);
    // Clear existing PDF URL when starting new generation
    setPdfUrl(null);
    setIsPolling(false);
    setPollingAttempts(0);
    
    if (!item?.id || !selectedPrintType) {
      setIsLoading(false);
      return;
    }

    const payload = {
      id: item.id,
      params: {
        [printTypeParamName]: selectedPrintType,
        include_images: includeImages,
      },
    };

    try {
      const response = await getPdfFunction(payload);
      console.log("=== PDF GENERATION RESPONSE ===");
      console.log("Full response:", response);
      console.log("response.data:", response?.data);
      console.log("response.data?.job_id:", response?.data?.job_id);
      console.log("response.data?.pdf_url:", response?.data?.pdf_url);
      
      // Check multiple possible response structures
      let pdfUrl = response?.data?.pdf_url || 
                   response?.data?.data?.pdf_url ||
                   response?.pdf_url ||
                   response?.data?.pdfUrl;
      
      // If we got a job_id instead, poll for status
      const jobId = response?.data?.job_id || response?.job_id;
      console.log("=== EXTRACTED VALUES ===");
      console.log("Job ID:", jobId);
      console.log("PDF URL:", pdfUrl);
      console.log("Will poll?", jobId && !pdfUrl);
      
      if (jobId && !pdfUrl) {
        // Stop loading spinner, start polling indicator
        setIsLoading(false);
        setIsPolling(true);
        setPollingAttempts(0);
        
        console.log("=== STARTING POLLING ===");
        console.log("Job ID:", jobId);
        console.log("Max attempts: 60, interval: 2000ms (2 seconds)");
        // Poll for job status (max 120 seconds, check every 2 seconds)
        const maxAttempts = 60;
        let attempts = 0;
        
        while (attempts < maxAttempts && !pdfUrl) {
          await new Promise(resolve => setTimeout(resolve, 2000)); // Poll every 2 seconds
          attempts++;
          setPollingAttempts(attempts);
          
          console.log(`=== POLL ATTEMPT ${attempts}/${maxAttempts} ===`);
          console.log("Calling checkPdfStatus with jobId:", jobId);
          
          try {
            // Use imported checkPdfStatus function
            console.log("Calling checkPdfStatus with jobId:", jobId);
            const statusResponse = await checkPdfStatus(jobId);
            console.log(`Poll attempt ${attempts} - Full response:`, statusResponse);
            console.log(`Poll attempt ${attempts} - response.data:`, statusResponse?.data);
            console.log(`Poll attempt ${attempts} - job_status:`, statusResponse?.data?.job_status);
            console.log(`Poll attempt ${attempts} - metadata:`, statusResponse?.data?.metadata);
            
            const jobStatus = statusResponse?.data?.job_status;
            const metadata = statusResponse?.data?.metadata;
            
            console.log(`Poll attempt ${attempts}: status=${jobStatus}, metadata=`, metadata);
            
            if (jobStatus === 'completed') {
              // Try multiple ways to get pdf_url from metadata
              const pdfUrlFromMetadata = metadata?.pdf_url || 
                                        metadata?.pdfUrl ||
                                        (typeof metadata === 'string' ? JSON.parse(metadata)?.pdf_url : null);
              
              if (pdfUrlFromMetadata) {
                pdfUrl = pdfUrlFromMetadata;
                console.log("PDF generation completed, URL:", pdfUrl);
                setIsPolling(false);
                setPollingAttempts(0);
                break;
              } else {
                console.warn("Job completed but no pdf_url in metadata:", metadata);
                // Continue polling a bit more in case metadata hasn't been saved yet
                if (attempts < 10) {
                  continue;
                }
              }
            } else if (jobStatus === 'failed') {
              setIsPolling(false);
              setPollingAttempts(0);
              const errorMsg = metadata?.error || metadata?.message || t("PDF generation failed");
              throw new Error(errorMsg);
            } else if (jobStatus === 'processing' || jobStatus === 'queued') {
              console.log(`PDF generation ${jobStatus} (attempt ${attempts}/${maxAttempts})...`);
            } else {
              console.log(`Unknown job status: ${jobStatus}`);
            }
          } catch (statusErr) {
            console.error(`Poll attempt ${attempts} failed:`, statusErr);
            
            // If it's a network error or 404, continue polling for a while
            if (statusErr.response?.status === 404) {
              // If 404, job might not exist yet, continue polling for first few attempts
              if (attempts < 10) {
                console.log("Job not found yet (404), continuing to poll...");
                continue;
              } else {
                // After 10 attempts, assume job doesn't exist
                setIsPolling(false);
                setPollingAttempts(0);
                throw new Error(t("PDF generation job not found"));
              }
            }
            
            // If status check fails with other errors, continue polling unless it's a clear failure
            if (statusErr.message?.includes("failed") && !statusErr.message?.includes("timeout")) {
              setIsPolling(false);
              setPollingAttempts(0);
              throw statusErr;
            }
            
            // For network errors or other issues, continue polling
            if (attempts >= maxAttempts) {
              setIsPolling(false);
              setPollingAttempts(0);
              throw new Error(t("PDF generation timed out after 30 seconds"));
            }
            
            // Continue polling for other errors
            console.log(`Continuing to poll despite error (attempt ${attempts}/${maxAttempts})...`);
          }
        }
        
        if (!pdfUrl) {
          console.error("PDF generation timed out after", attempts, "attempts");
          setIsPolling(false);
          setPollingAttempts(0);
          throw new Error(t("PDF generation timed out"));
        }
      } else {
        // If we got pdfUrl directly, stop loading
        setIsLoading(false);
      }
      
      if (pdfUrl) {
        console.log("Setting PDF URL:", pdfUrl);
        setPdfUrl(pdfUrl);
      } else {
        console.error("No pdf_url found in response:", response);
        toast.error(t("Failed to generate PDF - no URL returned"));
      }
    } catch (err) {
      console.error("Error fetching PDF:", err);
      setIsPolling(false);
      setPollingAttempts(0);
      toast.error(err.message || t("Failed to generate PDF"));
    } finally {
      setIsLoading(false);
    }
  }, [item?.id, selectedPrintType, includeImages, printTypeParamName, getPdfFunction, t]);

  const handlePrintTypeChange = useCallback((data) => {
    const value = data?.value;
    // Only set if value is not empty
    if (value && value !== "") {
      setSelectedPrintType(value);
      // If customer_report is selected and existingPdfUrl exists, use it
      if (value === "customer_report" && existingPdfUrl) {
        setPdfUrl(existingPdfUrl);
      } else {
        // Clear PDF URL when type changes so user needs to regenerate
        setPdfUrl(null);
      }
    } else {
      setSelectedPrintType(null);
      setPdfUrl(null);
    }
    // IMPORTANT: Do NOT call fetchPdf here - it should only be called from handleGenerate button
    // IMPORTANT: Do NOT trigger any form submission or API calls here
  }, [existingPdfUrl]);

  const handleIncludeImagesChange = (value) => {
    setIncludeImages(value);
    // Clear PDF URL when include images changes so user needs to regenerate
    setPdfUrl(null);
  };

  const handleGenerate = useCallback(() => {
    if (!selectedPrintType || selectedPrintType === "") {
      toast.error(t("Please select a print type"));
      return;
    }
    // This is the ONLY place fetchPdf should be called
    fetchPdf();
  }, [selectedPrintType, fetchPdf, t]);

  const handleDownload = () => {
    if (pdfUrl) {
      window.open(pdfUrl, "_blank");
    }
  };

  const handlePrint = async (values, helpers) => {
    try {
      if (!selectedPrintType) {
        toast.error(t("Please select a print type"));
        return;
      }

      const payload = {
        id: item.id,
        [printTypeParamName]: selectedPrintType,
        include_images: includeImages,
      };

      toast.promise(printFunction(payload), {
        loading: t("Saving...."),
        success: (data) => {
          if (data?.status === 500) {
            throw new Error("Server error");
          }
          if (data?.status !== 200 && data?.status !== 201) {
            throw new Error(mapErrors(data?.data?.errors || "Unknown error"));
          }
          if (onRefresh) {
            onRefresh(data?.payload || data?.data);
          }
          onClose();
          return t("Data was saved!");
        },
        error: (error) => {
          console.error(error);
          return t("Something went wrong. Could not save data!");
        },
      });
      helpers.setStatus({ success: true });
      helpers.setSubmitting(false);
      helpers.resetForm(initialValues);
    } catch (err) {
      toast.error("Something went wrong...");
      console.error(err);
      helpers.setStatus({ success: false });
      helpers.setErrors({ submit: err.message });
      helpers.setSubmitting(false);
    }
  };

  return (
    <>
      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={onClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="w-full sm:max-w-2xl max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="mt-3 sm:mt-5">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-medium leading-6 text-gray-900"
                    >
                      {title}
                    </Dialog.Title>

                    {!item?.id ? (
                      <div className="mt-4 text-red-600">
                        {t("Missing ID")}
                      </div>
                    ) : (
                      <Formik
                        initialValues={initialValues}
                        onSubmit={handlePrint}
                        enableReinitialize={false}
                      >
                        {(formik) => (
                          <Form onSubmit={(e) => {
                            e.preventDefault();
                            // Prevent automatic form submission
                          }}>
                            <div className="grid grid-cols-6 gap-6 mt-10">
                              <div className="col-span-6 sm:col-span-6">
                                <PMListboxFormik
                                  label={t("Print Type")}
                                  name={printTypeParamName}
                                  onChange={(data) => {
                                    // Only call handlePrintTypeChange, don't trigger any generation
                                    handlePrintTypeChange(data);
                                  }}
                                  options={printTypeOptions}
                                  disabled={isLoading}
                                  values={formik.values}
                                  setFieldValue={(name, value) => {
                                    // Update formik value but don't trigger form submission
                                    formik.setFieldValue(name, value, false);
                                  }}
                                />
                              </div>

                              <div className="col-span-6 sm:col-span-6">
                                <PMSwitch
                                  label={t("Include Images (as ZIP)")}
                                  name="include_images"
                                  checked={includeImages}
                                  onChange={(e) =>
                                    handleIncludeImagesChange(e.target.checked)
                                  }
                                  disabled={isLoading}
                                  {...formik}
                                />
                                <p className="mt-1 text-sm text-gray-500">
                                  {t(
                                    "If enabled, images will be included as attachments in a ZIP file"
                                  )}
                                </p>
                              </div>

                              <div className="col-span-6 sm:col-span-6">
                                {/* Show generate button only if no existing PDF or not customer_report type */}
                                {!(selectedPrintType === "customer_report" && existingPdfUrl && pdfUrl) && (
                                  <button
                                    type="button"
                                    disabled={isLoading || isPolling || !selectedPrintType || selectedPrintType === ""}
                                    className="w-full justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleGenerate();
                                    }}
                                  >
                                    {isLoading ? (
                                      <span className="flex items-center justify-center">
                                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                                        {t("Generating...")}
                                      </span>
                                    ) : selectedPrintType === "customer_report" && existingPdfUrl ? (
                                      t("Generate New PDF")
                                    ) : (
                                      t("Generate PDF")
                                    )}
                                  </button>
                                )}
                                {isPolling && (
                                  <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
                                    <p className="text-sm text-blue-800 text-center flex items-center justify-center">
                                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent mr-2"></div>
                                      {t("PDF generation queued. Waiting for completion...")} ({pollingAttempts}/60)
                                    </p>
                                  </div>
                                )}
                                {selectedPrintType === "customer_report" && existingPdfUrl && pdfUrl && (
                                  <p className="mt-1 text-sm text-green-600 text-center mb-2">
                                    {t("Existing customer report PDF is available for download below")}
                                  </p>
                                )}
                                {!selectedPrintType || selectedPrintType === "" ? (
                                  <p className="mt-1 text-xs text-gray-400 text-center">
                                    {t("Please select a print type to enable generation")}
                                  </p>
                                ) : null}
                              </div>

                              <div className="col-span-6 sm:col-span-6 mb-20">
                                {pdfUrl ? (
                                  <button
                                    type="button"
                                    className="w-full justify-center rounded-md border border-transparent bg-emerald-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm"
                                    onClick={handleDownload}
                                  >
                                    {includeImages
                                      ? t("Download ZIP")
                                      : t("Download PDF")}
                                  </button>
                                ) : null}
                              </div>
                            </div>

                            <div className="mt-20 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                              <button
                                type="button"
                                className="inline-flex justify-center w-20 rounded-md border border-transparent bg-gray-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm"
                                onClick={onClose}
                              >
                                {t("Close")}
                              </button>
                            </div>
                          </Form>
                        )}
                      </Formik>
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};
